import React, { useState, useContext, useEffect } from 'react';
import EditableField from '../../components/shared/editable-input';
import { RxCross2 } from "react-icons/rx";
import ModalHeading from '../../components/shared/modal-heading';
import { AccountsContext } from '../../components/context/account-context';
import { prependDefault } from '../../utils/structure-options-utils';
import { updateServiceConfig } from '../../services/service-config-service';

const EditModal = ({ selectedRow, setSelectedRow, setIsEditModalOpen, setData, isVehicleTab = false, vehicles }) => {
    const { accountId } = useContext(AccountsContext);
    const [updateStatus, setUpdateStatus] = useState({ message: '', type: '' });
    const [loadingFields, setLoadingFields] = useState({});

    useEffect(() => {
        let timer;
        if (updateStatus.message && updateStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUpdateStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [updateStatus]);

    const handleFieldUpdate = async (value, fieldName) => {
        setLoadingFields(prev => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        const fieldMap = {
            vehicle: 'vehicleId',
            serviceType: 'serviceType',
            triggerType: 'triggerType',
            start: 'start',
            period: 'period',
            status: 'status',
            note: 'note'
        };

        const payload = {};
        if (fieldName === 'start' && selectedRow.triggerType === 'serverTime') {
            // Convert datetime-local to Unix timestamp (seconds)
            payload[fieldMap[fieldName]] = Math.floor(new Date(value).getTime() / 1000).toString();
        } else if (fieldName === 'start') {
            // Handle start as a number for non-serverTime trigger types
            payload[fieldMap[fieldName]] = parseInt(value, 10);
        } else if (fieldName === 'vehicle') {
            payload[fieldMap[fieldName]] = value;
        } else if (fieldName === 'period') {
            payload[fieldMap[fieldName]] = parseInt(value, 10);
        } else {
            payload[fieldMap[fieldName]] = value;
        }

        try {
            console.log("Payload : ", payload)
            const response = await updateServiceConfig(accountId, selectedRow.serviceConfId, payload);
            if (response.success) {
                const updatedRow = { ...selectedRow };
                switch (fieldName) {
                    case 'vehicle':
                        updatedRow.vehicle = {
                            vehicleId: value,
                            name: vehicleOptions.find(opt => opt.value === value)?.label || ''
                        };
                        break;
                    case 'start':
                        updatedRow.start = selectedRow.triggerType === 'serverTime'
                            ? payload.start
                            : parseInt(value, 10);
                        break;
                    case 'period':
                        updatedRow.period = parseInt(value, 10);
                        break;
                    default:
                        updatedRow[fieldName] = value;
                        break;
                }
                setSelectedRow(updatedRow);
                setData(prevData => prevData.map(row =>
                    row.serviceConfId === selectedRow.serviceConfId ? updatedRow : row
                ));
                setUpdateStatus({
                    message: `${labelMap[fieldName] || fieldName} updated successfully!`,
                    type: 'success'
                });
            } else {
                setUpdateStatus({
                    message: response.data?.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error'
                });
                throw new Error('API update failed');
            }
        } catch (error) {
            setUpdateStatus({
                message: error.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error'
            });
            throw error;
        } finally {
            setLoadingFields(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const labelMap = {
        vehicle: 'Vehicle',
        serviceType: 'Service Type',
        triggerType: 'Trigger Type',
        start: 'Start',
        period: 'Period',
        status: 'Status',
        note: 'Note'
    };

    const vehicleOptions = prependDefault(vehicles.map(v => ({ value: v.vehicleId, label: v.label })), 'Vehicle');

    console.log(selectedRow)

    return (
        <div className="space-y-3 px-4 pt-3 relative">
            <ModalHeading className="text-left mb-3">Service Detail Information</ModalHeading>

            <button
                className='hover:scale-105 absolute -top-3 -right-2 bg-white border p-2 rounded-full'
                onClick={() => setIsEditModalOpen(false)}
            >
                <RxCross2 className='text-xl' />
            </button>

            {(updateStatus.message) && (
                <div className={`px-4 py-2 text-sm ${(updateStatus.type) === 'success' ? 'text-green-600 bg-green-100' :
                    (updateStatus.type) === 'error' ? 'text-red-600 bg-red-100' :
                        'text-blue-600 bg-blue-100'
                    } rounded-md mx-4`}>
                    {updateStatus.message}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <EditableField
                    label="Vehicle"
                    name="vehicle"
                    type='select'
                    value={selectedRow?.vehicleId?.vehicleId || ''}
                    options={vehicleOptions}
                    onChange={handleFieldUpdate}
                    isEditable={!isVehicleTab}
                    isLoading={loadingFields.vehicle}
                />

                <EditableField
                    label="Service Type"
                    name="serviceType"
                    type='select'
                    value={selectedRow?.serviceType || ''}
                    onChange={handleFieldUpdate}
                    isEditable={false}
                />

                <EditableField
                    label="Trigger Type"
                    name="triggerType"
                    type='select'
                    value={selectedRow?.triggerType || ''}
                    onChange={handleFieldUpdate}
                    isEditable={false}
                />

                {selectedRow.triggerType === 'serverTime' ? (
                    <EditableField
                        label="Start"
                        name="start"
                        value={selectedRow?.start || ''}
                        onChange={handleFieldUpdate}
                        type="datetime-local"
                        isLoading={loadingFields.start}
                    />
                ) : (
                    <EditableField
                        label="Start"
                        name="start"
                        value={selectedRow?.start || ''}
                        onChange={handleFieldUpdate}
                        type="number"
                        isLoading={loadingFields.start}
                    />
                )}

                <EditableField
                    label="Period (km/mi, hours, days)"
                    name="period"
                    value={selectedRow?.period || ''}
                    onChange={handleFieldUpdate}
                    type="number"
                    isLoading={loadingFields.period}
                />

                <EditableField
                    label="Status"
                    name="status"
                    value={selectedRow?.status || ''}
                    onChange={handleFieldUpdate}
                    type="select"
                    isEditable={false}
                    isLoading={loadingFields.status}
                />

                <EditableField
                    label="Note"
                    name="note"
                    type='textarea'
                    rows={2}
                    value={selectedRow?.note || ''}
                    onChange={handleFieldUpdate}
                    isLoading={loadingFields.note}
                />
            </div>
        </div>
    );
};

export default EditModal;